// KakaoMapModal.js
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, Crosshair, Filter } from 'lucide-react';
import { KAKAO_JAVASCRIPT_KEY } from '../config';
import {
    geocodeCustomerBatch,
    getGeocodedCustomers,
    getCustomersNeedingGeocoding,
    isCustomerGeocoded
} from '../utils/geocoding';
import { idbPutCustomer, idbGetCustomersByStage } from '../utils/idb';
import { getCustomerStages } from '../utils/customerStages';
import { useActionLog } from '../hooks/useActionLog';

// 카카오 지도 SDK 로더 (모달 전용)
function loadKakaoMapsSdk(apiKey) {
    return new Promise((resolve, reject) => {
        if (window.kakao && window.kakao.maps) {
            try {
                const maps = window.kakao.maps;
                if (typeof maps.load === 'function' && !maps.LatLng) {
                    maps.load(() => resolve(window.kakao));
                    return;
                }
            } catch (_) { }
            resolve(window.kakao);
            return;
        }
        const existing = document.querySelector('script[data-kakao-maps-sdk]');
        if (existing) {
            const handleLoaded = () => {
                try {
                    if (!window.kakao) { reject(new Error('Kakao SDK load failed')); return; }
                    const maps = window.kakao.maps;
                    if (typeof maps.load === 'function') {
                        maps.load(() => resolve(window.kakao));
                    } else {
                        resolve(window.kakao);
                    }
                } catch (e) { reject(e); }
            };
            if (existing.getAttribute('data-loaded') === 'true' || existing.readyState === 'complete') {
                handleLoaded();
                return;
            }
            existing.addEventListener('load', () => {
                existing.setAttribute('data-loaded', 'true');
                handleLoaded();
            });
            existing.addEventListener('error', (e) => {
                console.error('[KakaoMaps] existing script load error', e);
                reject(new Error('Kakao SDK script load failed (existing). 도메인 허용 여부와 앱 키를 확인하세요.'));
            });
            return;
        }
        const script = document.createElement('script');
        script.setAttribute('data-kakao-maps-sdk', 'true');
        script.defer = true;
        script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${apiKey}&autoload=false&libraries=services`;
        script.onload = () => {
            script.setAttribute('data-loaded', 'true');
            if (!window.kakao) { reject(new Error('Kakao SDK load failed')); return; }
            window.kakao.maps.load(() => resolve(window.kakao));
        };
        script.onerror = (e) => {
            console.error('[KakaoMaps] script error:', e);
            const origin = window.location.origin;
            reject(new Error('Kakao SDK 로드 실패. 등록 도메인 확인: ' + origin));
        };
        document.head.appendChild(script);
    });
}

const KakaoMapModal = ({ isOpen, onClose }) => {
    const { sendActionLog } = useActionLog();
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const [error, setError] = useState(null);
    const [hasGeo, setHasGeo] = useState(false);
    const lastPosRef = useRef(null); // { lat, lng }

    // 고객 마커 관련 상태
    const customerMarkersRef = useRef([]);
    const infoWindowRef = useRef(null);
    const [geocodingProgress, setGeocodingProgress] = useState({ current: 0, total: 0, isComplete: false });
    const [isGeocoding, setIsGeocoding] = useState(false);
    const [showToast, setShowToast] = useState(false);

    // 필터 관련 상태
    const allStageIds = ['prospect', 'preparation', 'appointment', 'consultation', 'closed', 'rejected', 'bank'];
    const [selectedStages, setSelectedStages] = useState(new Set(allStageIds)); // 기본값: 모든 단계 선택
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [stages, setStages] = useState([]);
    const kakaoRef = useRef(null); // kakao 인스턴스 저장

    // 고객 마커 정리 함수
    const clearCustomerMarkers = useCallback(() => {
        try {
            (customerMarkersRef.current || []).forEach(marker => marker.setMap(null));
        } catch (_) { }
        customerMarkersRef.current = [];

        // InfoWindow도 닫기
        if (infoWindowRef.current) {
            infoWindowRef.current.close();
            infoWindowRef.current = null;
        }
    }, []);

    // 고객 마커 생성 함수
    const createCustomerMarker = useCallback((customer, kakao) => {
        if (!customer.lat || !customer.lng) return null;

        const position = new kakao.maps.LatLng(customer.lat, customer.lng);
        const marker = new kakao.maps.Marker({
            position: position,
            map: mapRef.current
        });

        // 마커 클릭 시 InfoWindow 표시
        kakao.maps.event.addListener(marker, 'click', () => {
            // 지도 고객 마커 클릭 로깅
            sendActionLog('지도_고객마커클릭', {
                customerName: customer.name
            });
            
            if (infoWindowRef.current) {
                infoWindowRef.current.close();
            }

            // 고객 상세 모달 열기 함수
            const openCustomerDetail = () => {
                try {
                    sendActionLog('지도_고객상세모달열기', {
                        customerName: customer.name
                    });
                    window.dispatchEvent(new CustomEvent('openCustomerOverlayFromAssistant', { 
                        detail: { customer } 
                    }));
                    // InfoWindow 닫기
                    if (infoWindowRef.current) {
                        infoWindowRef.current.close();
                    }
                } catch (e) {
                    console.error('고객 상세 모달 열기 실패:', e);
                }
            };

            // 고유한 ID 생성하여 이벤트 리스너 중복 방지
            const buttonId = `customer-detail-btn-${customer.id}`;

            // 단계를 한글로 변환
            const businessType = localStorage.getItem('businessType') || 'insurance';
            const customerStages = getCustomerStages(businessType);
            const stageInfo = customerStages.find(s => s.id === customer.stage);
            const stageDisplayName = stageInfo ? stageInfo.title : (customer.stage || '알 수 없음');

            const content = `
                <div style="padding: 10px; min-width: 200px;">
                    <div style="font-weight: bold; margin-bottom: 5px; color: #333;">
                        ${customer.name || '이름 없음'}
                    </div>
                    <div style="font-size: 12px; color: #666; margin-bottom: 3px;">
                        📞 ${customer.phone || '전화번호 없음'}
                    </div>
                    <div style="font-size: 12px; color: #666; margin-bottom: 3px;">
                        📍 ${customer.address || '주소 없음'}
                    </div>
                    <div style="font-size: 12px; color: #888; margin-bottom: 8px;">
                        단계: ${stageDisplayName}
                    </div>
                    <button 
                        id="${buttonId}"
                        style="
                            width: 100%;
                            padding: 8px 12px;
                            background-color: #3b82f6;
                            color: white;
                            border: none;
                            border-radius: 6px;
                            font-size: 13px;
                            font-weight: 500;
                            cursor: pointer;
                            transition: background-color 0.2s;
                        "
                        onmouseover="this.style.backgroundColor='#2563eb'"
                        onmouseout="this.style.backgroundColor='#3b82f6'"
                    >
                        상세보기
                    </button>
                </div>
            `;

            infoWindowRef.current = new kakao.maps.InfoWindow({
                content: content,
                removable: true
            });

            infoWindowRef.current.open(mapRef.current, marker);

            // InfoWindow가 열린 후 버튼에 이벤트 리스너 추가
            setTimeout(() => {
                const button = document.getElementById(buttonId);
                if (button) {
                    button.addEventListener('click', openCustomerDetail);
                }
            }, 100);
        });

        return marker;
    }, [sendActionLog]);

    // 고객 마커들을 지도에 표시
    const displayCustomerMarkers = useCallback((customersToShow, kakao, preserveBounds = false) => {
        clearCustomerMarkers();

        const markers = [];
        const bounds = new kakao.maps.LatLngBounds();
        let hasValidMarkers = false;

        customersToShow.forEach(customer => {
            const marker = createCustomerMarker(customer, kakao);
            if (marker) {
                markers.push(marker);
                bounds.extend(new kakao.maps.LatLng(customer.lat, customer.lng));
                hasValidMarkers = true;
            }
        });

        customerMarkersRef.current = markers;

        // 지도 bounds 조정 (마커가 모두 보이도록) - preserveBounds가 true면 스킵
        if (hasValidMarkers && markers.length > 0 && !preserveBounds) {
            mapRef.current.setBounds(bounds);
        }

        return markers.length;
    }, [clearCustomerMarkers, createCustomerMarker]);

    // IndexedDB에서 선택된 단계의 고객만 로드 (지도용)
    const loadAllCustomersForMap = useCallback(async () => {
        try {
            const allCustomers = [];
            // 선택된 단계만 로드
            const stagesToLoad = Array.from(selectedStages);

            for (const stage of stagesToLoad) {
                const customers = await idbGetCustomersByStage(stage, 0, 9999); // 모든 고객 로드
                allCustomers.push(...customers);
            }

            return allCustomers;
        } catch (error) {
            console.error('고객 로드 실패:', error);
            return [];
        }
    }, [selectedStages]);

    // 고객 데이터 처리 및 마커 표시
    const processCustomers = useCallback(async (kakao, preserveBounds = false) => {
        // IndexedDB에서 모든 고객 로드
        const allCustomers = await loadAllCustomersForMap();

        if (allCustomers.length === 0) {
            setGeocodingProgress({ current: 0, total: 0, isComplete: true, displayedCount: 0 });
            return;
        }

        // Phase 1: 이미 지오코딩된 고객들 즉시 표시
        const geocodedCustomers = getGeocodedCustomers(allCustomers);
        const immediateCount = displayCustomerMarkers(geocodedCustomers, kakao, preserveBounds);

        console.log(`즉시 표시된 고객: ${immediateCount}명`);

        // Phase 2: 지오코딩이 필요한 고객들 점진적 처리
        const customersNeedingGeocoding = getCustomersNeedingGeocoding(allCustomers);

        if (customersNeedingGeocoding.length === 0) {
            const displayedCustomerCount = customerMarkersRef.current.length;
            setGeocodingProgress({ current: allCustomers.length, total: allCustomers.length, isComplete: true, displayedCount: displayedCustomerCount });
            return;
        }

        setIsGeocoding(true);
        setGeocodingProgress({ current: geocodedCustomers.length, total: allCustomers.length, isComplete: false });

        try {
            await geocodeCustomerBatch(
                customersNeedingGeocoding,
                async (current, total, geocodedCustomer) => {
                    // 진행률 업데이트
                    setGeocodingProgress({ current, total, isComplete: false });

                    // 지오코딩된 고객을 IndexedDB에 저장
                    if (isCustomerGeocoded(geocodedCustomer)) {
                        try {
                            await idbPutCustomer(geocodedCustomer);
                        } catch (e) {
                            console.warn('고객 좌표 저장 실패:', e);
                        }
                    }

                    // 마커 즉시 추가
                    const marker = createCustomerMarker(geocodedCustomer, kakao);
                    if (marker) {
                        customerMarkersRef.current.push(marker);

                        // 지도 bounds 재조정
                        const bounds = new kakao.maps.LatLngBounds();
                        customerMarkersRef.current.forEach(m => {
                            bounds.extend(m.getPosition());
                        });
                        mapRef.current.setBounds(bounds);
                    }
                },
                5, // 배치 크기
                200 // 지연 시간
            );

            // 지도에 표시된 고객 수 계산
            const displayedCustomerCount = customerMarkersRef.current.length;
            setGeocodingProgress({ current: allCustomers.length, total: allCustomers.length, isComplete: true, displayedCount: displayedCustomerCount });

            // 완료 토스트 표시
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000); // 3초 후 자동 숨김

        } catch (error) {
            console.error('고객 지오코딩 실패:', error);
            const displayedCustomerCount = customerMarkersRef.current.length;
            setGeocodingProgress({ current: geocodedCustomers.length, total: allCustomers.length, isComplete: true, displayedCount: displayedCustomerCount });
        } finally {
            setIsGeocoding(false);
        }
    }, [loadAllCustomersForMap, displayCustomerMarkers, createCustomerMarker]);

    // 고객 단계 정보 초기화
    useEffect(() => {
        if (isOpen) {
            const businessType = localStorage.getItem('businessType') || 'insurance';
            const customerStages = getCustomerStages(businessType);
            setStages(customerStages);
        }
    }, [isOpen]);


    // 필터 변경 시 지도 업데이트 (selectedStages를 문자열로 변환하여 dependency로 사용)
    useEffect(() => {
        if (isOpen && kakaoRef.current && mapRef.current) {
            // 필터가 변경되면 고객 마커 재표시
            processCustomers(kakaoRef.current);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Array.from(selectedStages).sort().join(','), processCustomers]);

    // 단계 이동 후 지도 마커 업데이트
    useEffect(() => {
        const handleRefreshMarkers = () => {
            if (isOpen && kakaoRef.current && mapRef.current) {
                // 고객 마커 재표시 (bounds는 유지하여 지도가 깜빡이지 않도록)
                processCustomers(kakaoRef.current, true);
            }
        };
        
        window.addEventListener('refreshKakaoMapMarkers', handleRefreshMarkers);
        return () => {
            window.removeEventListener('refreshKakaoMapMarkers', handleRefreshMarkers);
        };
    }, [isOpen, processCustomers]);

    useEffect(() => {
        let mapInstance = null;
        if (!isOpen) return;
        (async () => {
            try {
                if (!KAKAO_JAVASCRIPT_KEY) {
                    throw new Error('Kakao JavaScript 키가 설정되지 않았습니다.');
                }
                const kakao = await loadKakaoMapsSdk(KAKAO_JAVASCRIPT_KEY);
                kakaoRef.current = kakao; // kakao 인스턴스 저장
                if (!containerRef.current) return;

                // 1) 지도를 즉시 기본 위치로 렌더링 (iOS 권한 팝업 대기와 무관하게 먼저 표시)
                const defaultCenter = new kakao.maps.LatLng(37.5665, 126.9780); // 서울시청
                mapInstance = new kakao.maps.Map(containerRef.current, {
                    center: defaultCenter,
                    level: 6, // 스케일바를 위한 줌 레벨
                    scaleControl: true // 스케일 바 표시
                });
                markerRef.current = new kakao.maps.Marker({ position: defaultCenter, map: mapInstance });
                mapRef.current = mapInstance;

                // 지도 생성 후 줌 레벨 강제 설정
                setTimeout(() => {
                    if (mapInstance && typeof mapInstance.relayout === 'function') {
                        mapInstance.relayout();
                        mapInstance.setLevel(6); // 줌 레벨 강제 설정
                    }
                }, 100);


                // 2) 고객 마커 표시 (하이브리드 로딩)
                await processCustomers(kakaoRef.current);

                // 3) 현재 위치 획득은 비동기로 시도하고, 성공 시 재중심
                const tryCurrent = () => new Promise((resolve, reject) => {
                    if (!('geolocation' in navigator)) { reject(new Error('geolocation 미지원')); return; }
                    navigator.geolocation.getCurrentPosition(
                        pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                        err => reject(err),
                        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
                    );
                });
                tryCurrent()
                    .then(({ lat, lng }) => {
                        const ll = new kakao.maps.LatLng(lat, lng);
                        mapInstance.setCenter(ll);
                        if (markerRef.current) markerRef.current.setPosition(ll);
                        setHasGeo(true);
                        lastPosRef.current = { lat, lng };
                    })
                    .catch(() => {
                        setHasGeo(false);
                    });
            } catch (e) {
                console.error(e);
                setError(e?.message || String(e));
            }
        })();
        return () => {
            mapInstance = null;
            mapRef.current = null;
            markerRef.current = null;
            kakaoRef.current = null;
            clearCustomerMarkers();
        };
    }, [isOpen, processCustomers, clearCustomerMarkers]);
    // const loadCustomerPins = async () => { /* 정교화 작업 후 재활성화 예정 */ };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[1001]">
            {/* 배경 */}
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />

            {/* 바텀시트 */}
            <div className="absolute left-0 right-0 bottom-0 bg-white rounded-t-2xl shadow-2xl overflow-hidden" style={{ height: '68vh' }}>
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <div className="h-1.5 w-12 bg-gray-300 rounded-full mx-auto absolute left-1/2 -translate-x-1/2 -top-2" />
                    <h3 className="text-base font-bold">지도</h3>
                    <div className="flex items-center gap-2">
                        {/* 필터 버튼 */}
                        <button
                            onClick={() => {
                                sendActionLog('지도_필터열기');
                                setIsFilterOpen(!isFilterOpen);
                            }}
                            className="px-3 py-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-sm flex items-center gap-2"
                        >
                            <Filter className="w-4 h-4" /> 필터
                        </button>
                        {/* 지오코딩 진행률 표시 - 헤더에 통합 */}
                        {isGeocoding && geocodingProgress.total > 0 && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                                <span>로딩 중 {geocodingProgress.current}/{geocodingProgress.total}</span>
                            </div>
                        )}
                        {geocodingProgress.isComplete && geocodingProgress.total > 0 && (
                            <div className="text-sm text-green-600">
                                ✓ 완료 ({geocodingProgress.displayedCount || 0}명)
                            </div>
                        )}
                        <button
                            onClick={() => {
                                sendActionLog('지도_현재위치찾기');
                                try {
                                    const kakao = window.kakao;
                                    if (!kakao || !mapRef.current) return;
                                    const getCurrent = () => new Promise((resolve, reject) => {
                                        if (!('geolocation' in navigator)) { reject(new Error('geolocation 미지원')); return; }
                                        navigator.geolocation.getCurrentPosition(
                                            pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                                            err => reject(err),
                                            { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
                                        );
                                    });
                                    // 캐시 즉시 이동
                                    if (lastPosRef.current) {
                                        const cached = new kakao.maps.LatLng(lastPosRef.current.lat, lastPosRef.current.lng);
                                        mapRef.current.setCenter(cached);
                                        mapRef.current.setLevel(6); // 스케일바를 위한 줌 레벨
                                        if (markerRef.current) markerRef.current.setPosition(cached);
                                    }
                                    // 최신 위치 갱신
                                    getCurrent()
                                        .then(({ lat, lng }) => {
                                            const ll = new kakao.maps.LatLng(lat, lng);
                                            mapRef.current.setCenter(ll);
                                            mapRef.current.setLevel(6); // 4km 스케일바를 위한 줌 레벨
                                            if (markerRef.current) markerRef.current.setPosition(ll);
                                            setHasGeo(true);
                                            lastPosRef.current = { lat, lng };
                                        })
                                        .catch(() => {
                                            const fallback = new kakao.maps.LatLng(37.5665, 126.9780);
                                            mapRef.current.setCenter(fallback);
                                            mapRef.current.setLevel(6); // 4km 스케일바를 위한 줌 레벨
                                            if (markerRef.current) markerRef.current.setPosition(fallback);
                                            setHasGeo(false);
                                        });
                                } catch (_) { }
                            }}
                            className="px-3 py-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-sm flex items-center gap-2"
                        >
                            <Crosshair className="w-4 h-4" /> {hasGeo ? '현 위치' : '재중심'}
                        </button>
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                            <X className="w-5 h-5 text-gray-700" />
                        </button>
                    </div>
                </div>
                {error ? (
                    <div className="h-full flex items-center justify-center text-sm text-red-600 px-4">
                        {error}
                    </div>
                ) : (
                    <div className="relative h-[calc(68vh-70px)]">
                        <div ref={containerRef} style={{ width: '100%', height: '100%' }} />

                        {/* 필터 드롭다운 - 지도 중앙 모달 */}
                        {isFilterOpen && (
                            <>
                                {/* 반투명 배경 오버레이 */}
                                <div
                                    className="absolute inset-0 bg-black/30 z-[1002]"
                                    onClick={() => setIsFilterOpen(false)}
                                />
                                {/* 드롭다운 모달 */}
                                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white border border-gray-200 rounded-lg shadow-xl z-[1003] min-w-[250px] max-w-[90%] max-h-[400px] overflow-y-auto">
                                    <div className="p-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <h4 className="font-bold text-gray-800">단계 필터</h4>
                                            <button
                                                onClick={() => setIsFilterOpen(false)}
                                                className="p-1 hover:bg-gray-100 rounded"
                                            >
                                                <X className="w-4 h-4 text-gray-600" />
                                            </button>
                                        </div>
                                        <div className="space-y-1">
                                            {stages.map((stage) => {
                                                const isSelected = selectedStages.has(stage.id);
                                                return (
                                                    <label
                                                        key={stage.id}
                                                        className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={(e) => {
                                                                const newSelected = new Set(selectedStages);
                                                                if (e.target.checked) {
                                                                    newSelected.add(stage.id);
                                                                } else {
                                                                    // 최소 1개는 선택 유지
                                                                    if (newSelected.size > 1) {
                                                                        newSelected.delete(stage.id);
                                                                    } else {
                                                                        return; // 마지막 하나는 해제 불가
                                                                    }
                                                                }
                                                                setSelectedStages(newSelected);
                                                            }}
                                                            className="rounded"
                                                        />
                                                        <span className="text-sm">{stage.emoji}</span>
                                                        <span className="text-sm text-gray-700">{stage.title}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* 완료 토스트 메시지 */}
            {showToast && (
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[1002]">
                    <div className="bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2">
                        <span className="text-lg">✓</span>
                        <span className="font-medium">모든 고객이 지도에 표시되었습니다! ({geocodingProgress.displayedCount || 0}명)</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default KakaoMapModal;
